import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { staffApi, settingsApi } from '@/lib/api';
import { Mail, Phone, User } from 'lucide-react';

const AboutPage = () => {
  const [staff, setStaff] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [staffData, settingsData] = await Promise.all([
        staffApi.getAll().catch(() => []),
        settingsApi.get().catch(() => null)
      ]);
      setStaff(staffData);
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section 
        className="relative bg-gradient-to-r from-stone-800 to-stone-700 py-24"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=1920')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-stone-900/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            About Us
          </h1>
          <p className="text-xl text-stone-300 max-w-2xl mx-auto">
            Learn more about our church family and our mission
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg prose-stone max-w-none">
            <h2 className="text-3xl font-serif font-bold text-stone-800 mb-6 text-center">
              {settings?.church_name || "St. John's United Church of Christ"}
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed">
              St. John's UCC in Crown Point, IN is a conservative group of people who love God, 
              their families and community. We strive to meet folks at the point of their need 
              in sharing the good news of the gospel of Jesus Christ!
            </p>
            <p className="text-stone-600 text-lg leading-relaxed">
              We are compassionate followers of Jesus Christ doing our best to serve as God leads 
              with the needs of our members, families and community. Our heart's desire is to see 
              everyone come to know Jesus Christ as Lord and Savior!
            </p>
            <p className="text-amber-700 text-xl font-medium text-center mt-8">
              We are good people serving a GREAT GOD!
            </p>
          </div>
        </div>
      </section>

      {/* Service Times */}
      <section className="py-12 bg-amber-700">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl font-serif font-bold mb-4">Join Us for Worship</h2>
          <p className="text-xl">{settings?.service_times || 'Sunday Worship: 10:00 AM'}</p>
          <p className="mt-2 text-amber-100">{settings?.address || '1288 S. Indiana Ave, Crown Point, IN 46307'}</p>
        </div>
      </section>

      {/* Staff Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-serif font-bold text-stone-800 mb-12 text-center">
            Our Leadership Team
          </h2>
          
          {loading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
            </div>
          ) : staff.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {staff.map((member) => (
                <Card key={member.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-square bg-stone-200">
                    {member.photo_data || member.photo_url ? (
                      <img
                        src={member.photo_data || member.photo_url}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-amber-200">
                        <User className="h-24 w-24 text-amber-400" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-stone-800">{member.name}</h3>
                    <p className="text-amber-700 font-medium mb-3">{member.title}</p>
                    <p className="text-stone-600 text-sm mb-4">{member.bio}</p>
                    <div className="space-y-1 text-sm text-stone-500">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="flex items-center hover:text-amber-700">
                          <Mail className="h-4 w-4 mr-2" />
                          {member.email}
                        </a>
                      )}
                      {member.phone && (
                        <a href={`tel:${member.phone}`} className="flex items-center hover:text-amber-700">
                          <Phone className="h-4 w-4 mr-2" />
                          {member.phone}
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <p className="text-stone-600">Staff information coming soon.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Location */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-bold text-stone-800 mb-6">Find Us</h2>
          <p className="text-lg text-stone-600 mb-4">
            {settings?.address || '1288 S. Indiana Ave, Crown Point, IN 46307'}
          </p>
          <p className="text-stone-600">
            Phone: <a href={`tel:${settings?.phone || '219-663-1608'}`} className="text-amber-700 hover:underline">
              {settings?.phone || '219-663-1608'}
            </a>
          </p>
          <p className="text-stone-600">
            Email: <a href={`mailto:${settings?.email || 'cpuccsecretary@gmail.com'}`} className="text-amber-700 hover:underline">
              {settings?.email || 'cpuccsecretary@gmail.com'}
            </a>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
